import { Link } from 'react-router';
import { Box, Typography, Chip, Spinner, Alert, PanelCard } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { DASHBOARD_LIVE_TOOLS, DASHBOARD_ROADMAP_TOOLS, type AdminTool } from '../lib/adminTools';

/**
 * Vendor Admin dashboard — the landing page after the portal guard admits an
 * admin/trusted user. Composed from DSL primitives + PanelCard (the shared
 * top-accent card). "Your tools" surfaces the live tools the caller's
 * capabilities allow; "On the roadmap" previews what's coming.
 */

const ACCENT = '#000000';

/** Capability token pill — the `cap:scope` shown on each tool card. */
function CapabilityToken({ token, muted }: { token: string; muted?: boolean }) {
  return (
    <Chip
      label={token}
      size="small"
      backgroundColor={muted ? '#EEF0F2' : '#F1F4F2'}
      textColor={muted ? '#9aa3ab' : '#5a7063'}
      fontSize={11}
      sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', borderRadius: '6px' }}
    />
  );
}

function ToolBody({ tool, muted }: { tool: AdminTool; muted?: boolean }) {
  return (
    <>
      <Typography sx={{ fontSize: '14px', color: muted ? '#9aa3ab' : '#5a7063', lineHeight: 1.5 }}>
        {tool.description}
      </Typography>
      <Box sx={{ mt: 'auto', pt: 1 }}>
        <CapabilityToken token={tool.capability} muted={muted} />
      </Box>
    </>
  );
}

export default function HomePage() {
  const caps = useCapabilities();

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  if (caps.error) {
    return <Alert severity="error">Couldn’t load your capabilities: {caps.error}</Alert>;
  }

  const liveTools = DASHBOARD_LIVE_TOOLS.filter((t) => caps.has(t.capability));
  const roleLabel = caps.data?.role === 'admin' ? 'Administrator' : 'Trusted contributor';

  return (
    <Box>
      {/* Identity */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Vendor Admin
        </Typography>
        {caps.data && (
          <Chip
            label={roleLabel}
            size="small"
            backgroundColor="#2E9E3A"
            textColor="#ffffff"
            fontWeight={700}
          />
        )}
      </Box>
      <Typography sx={{ color: '#6b7280', mb: 4 }}>{caps.data?.email}</Typography>

      {/* Your tools */}
      <SectionLabel>Your tools</SectionLabel>
      {liveTools.length === 0 ? (
        <Alert severity="info">
          You’re signed in, but your account has no vendor tools enabled yet.
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 5,
          }}
        >
          {liveTools.map((tool) => (
            <Box
              key={tool.key}
              component={Link}
              to={tool.to as string}
              sx={{ textDecoration: 'none', display: 'block' }}
            >
              <PanelCard
                as="article"
                accent={ACCENT}
                title={tool.label}
                sx={{
                  height: 'auto',
                  minHeight: 140,
                  transition: 'box-shadow 0.15s, transform 0.15s',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: '0 6px 18px rgba(0,0,0,0.10)', transform: 'translateY(-1px)' },
                }}
              >
                <ToolBody tool={tool} />
              </PanelCard>
            </Box>
          ))}
        </Box>
      )}

      {/* On the roadmap */}
      {DASHBOARD_ROADMAP_TOOLS.length > 0 && (
        <>
          <SectionLabel>On the roadmap</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {DASHBOARD_ROADMAP_TOOLS.map((tool) => (
              <PanelCard
                key={tool.key}
                as="article"
                title={
                  <Typography component="span" sx={{ fontSize: '16px', fontWeight: 700, color: '#8a939b' }}>
                    {tool.label}
                  </Typography>
                }
                actions={
                  <Chip label="Soon" size="small" backgroundColor="#E7EAEC" textColor="#8a939b" fontSize={10} fontWeight={700} />
                }
                sx={{ height: 'auto', minHeight: 140, bgcolor: '#FAFBFC', borderStyle: 'dashed' }}
              >
                <ToolBody tool={tool} muted />
              </PanelCard>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: '#9aa3ab',
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}
